import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import useItemQuantities from '@afosto/utils-react/useItemQuantities';
import { getTotalQuantityInOtherParcels } from './utils';
import translations from './translations';

const usePickQuantities = (item, packingContext, onQuantityReached) => {
  const confirm = useConfirm();
  const intl = useIntl();
  const { currentParcel, setShowParcelsDialog } = packingContext ?? {};
  const itemKey = item?.id;

  const handleQuantityReached = async quantities => {
    if (onQuantityReached && typeof onQuantityReached === 'function') {
      return onQuantityReached(item, quantities);
    }

    return true;
  };

  const handleConfirmNoParcelQuantity = async () =>
    confirm({
      title: intl.formatMessage(translations.noParcelQuantityDialog.title),
      description: intl.formatMessage(translations.noParcelQuantityDialog.description),
      catchOnCancel: false,
      buttons: [
        {
          label: intl.formatMessage(translations.noParcelQuantityDialog.buttons.close),
          type: 'cancel',
          variant: 'text',
          color: 'default',
        },
        {
          label: intl.formatMessage(translations.noParcelQuantityDialog.buttons.changeParcels),
          response: 'changeParcel',
          type: 'submit',
          variant: 'text',
          color: 'primary',
        },
      ],
    });

  const handleConfirmLessThanParcelQuantity = async () =>
    confirm({
      title: intl.formatMessage(translations.lessThanParcelQuantityDialog.title),
      description: intl.formatMessage(translations.lessThanParcelQuantityDialog.description),
      catchOnCancel: false,
      disableBackdropClick: true,
      buttons: [
        {
          label: intl.formatMessage(translations.lessThanParcelQuantityDialog.buttons.close),
          type: 'cancel',
          variant: 'text',
          color: 'default',
        },
        {
          label: intl.formatMessage(
            translations.lessThanParcelQuantityDialog.buttons.changeParcels,
          ),
          response: 'changeParcel',
          type: 'submit',
          variant: 'text',
          color: 'primary',
        },
      ],
    });

  const itemQuantityCounter = useItemQuantities(
    item
      ? {
          [itemKey]: {
            requested: item.quantity ?? 0,
            quantity: item.handled_ids?.length ?? 0,
          },
        }
      : {},
    {
      onAll: async (key, newValue, updatedQuantities) => {
        const quantities = updatedQuantities[key];
        await handleQuantityReached(quantities);
      },
      onBeforeChange: async (key, newValue) => {
        if (currentParcel) {
          const quantityInOtherParcels = getTotalQuantityInOtherParcels(item, currentParcel);

          if (newValue <= quantityInOtherParcels) {
            const response = await handleConfirmLessThanParcelQuantity();

            if (response === 'changeParcel') {
              setShowParcelsDialog(true);
            }

            return quantityInOtherParcels;
          }
        }

        return newValue;
      },
      onBeforeMinus: async (key, newValue) => {
        if (currentParcel) {
          const quantityInOtherParcels = getTotalQuantityInOtherParcels(item, currentParcel);

          if (newValue < quantityInOtherParcels) {
            const response = await handleConfirmNoParcelQuantity();

            if (response === 'changeParcel') {
              setShowParcelsDialog(true);
            }

            return Promise.reject(new Error('No quantity available in current parcel'));
          }
        }

        return newValue;
      },
      onPlus: async (key, newValue, updatedQuantities) => {
        const quantities = updatedQuantities[key] ?? {};
        const { requested, quantity } = quantities;

        if (requested === quantity) {
          await handleQuantityReached(updatedQuantities[key]);
        }
      },
    },
  );

  return { itemKey, itemQuantityCounter };
};

export default usePickQuantities;
